import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Send,
  Check,
  Handshake,
  BookOpenText,
  ShieldCheck,
  ArrowRight,
  X,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CARD_SPONSOR, CARD_STORY } from "@/data/media";
import { InfoTip } from "@/components/InfoTip";

const EMPTY = {
  nome: "",
  azienda: "",
  email: "",
  telefono: "",
  settore: "",
  messaggio: "",
  privacy: false,
};

const inputCls =
  "peer w-full rounded-xl border border-[#E8E2E4] bg-white px-4 pb-2.5 pt-6 text-sm text-[#1A1617] outline-none transition-colors duration-300 focus:border-[#E0708A] focus:ring-2 focus:ring-[#E0708A]/30";

export const Field = ({ label, testId, textarea, ...rest }) => (
  <div className="relative">
    {textarea ? (
      <textarea rows={4} className={`${inputCls} resize-none`} data-testid={testId} {...rest} />
    ) : (
      <input className={inputCls} data-testid={testId} {...rest} />
    )}
    <span className="pointer-events-none absolute left-4 top-2 text-[11px] uppercase tracking-[0.14em] text-[#6B5E62]/70 transition-colors duration-300 peer-focus:text-[#E0708A]">
      {label}
    </span>
  </div>
);

export const PrivacyConsent = ({ checked, onChange, testId = "privacy-checkbox" }) => (
  <label
    className="flex cursor-pointer items-start gap-3 rounded-xl bg-[#FAF5F6] p-4 text-sm text-[#6B5E62]"
    data-testid={`${testId}-label`}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      data-testid={testId}
      className="mt-0.5 h-4 w-4 shrink-0 accent-[#E0708A]"
    />
    <span>
      Ho letto l'
      <Link
        to="/privacy"
        target="_blank"
        className="di-underline text-[#5E2A52]"
        data-testid={`${testId}-link`}
      >
        informativa privacy
      </Link>{" "}
      e acconsento al trattamento dei miei dati per gestire e rispondere a
      questa richiesta (art. 6.1.a GDPR). Titolare del trattamento: BC NEXT,
      Via Francesco Brighindi 37 — Frosinone, P.IVA 03291880601,
      info@bcnext.it. Posso revocare il consenso in qualsiasi momento. Nessuna
      storia viene pubblicata senza la mia approvazione e i dati non sono
      ceduti a terzi per finalità commerciali.
    </span>
  </label>
);

const SponsorForm = ({ onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      setError("Per procedere è necessario accettare l'informativa privacy.");
      toast.error("Per procedere è necessario accettare l'informativa privacy.");
      return;
    }
    setError("");
    setSending(true);
    try {
      const { privacy, ...rest } = form;
      await api.post("/applications", {
        ...rest,
        tipo: "sponsor",
        privacy_consent: privacy,
      });
      setDone(true);
      setForm(EMPTY);
      toast.success("Candidatura sponsor inviata! Ti ricontattiamo presto.");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      onSubmit={submit}
      data-testid="sponsor-form"
      className="grid gap-4 overflow-hidden border-t border-[#E8E2E4] p-7 sm:grid-cols-2 sm:p-10"
    >
      <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-3">
        <p className="inline-flex items-center gap-2 text-sm text-[#5E2A52]">
          <Handshake size={16} className="text-[#E0708A]" />
          Modulo <b>Diventa sponsor</b>
        </p>
        <button
          type="button"
          onClick={onClose}
          data-testid="sponsor-form-close"
          className="inline-flex items-center gap-1.5 text-xs text-[#6B5E62] di-underline"
        >
          <X size={12} /> Chiudi
        </button>
      </div>

      <Field required label="Nome e cognome *" testId="sponsor-input-nome" value={form.nome} onChange={set("nome")} />
      <Field required label="Azienda *" testId="sponsor-input-azienda" value={form.azienda} onChange={set("azienda")} />
      <Field required type="email" label="Email *" testId="sponsor-input-email" value={form.email} onChange={set("email")} />
      <Field label="Telefono" testId="sponsor-input-telefono" value={form.telefono} onChange={set("telefono")} />
      <div className="sm:col-span-2">
        <Field label="Settore" testId="sponsor-input-settore" value={form.settore} onChange={set("settore")} />
      </div>
      <div className="sm:col-span-2">
        <Field
          textarea
          label="Come vorresti sostenere la rete"
          testId="sponsor-input-messaggio"
          value={form.messaggio}
          onChange={set("messaggio")}
        />
      </div>
      <div className="sm:col-span-2">
        <PrivacyConsent
          checked={form.privacy}
          onChange={(v) => setForm({ ...form, privacy: v })}
          testId="sponsor-privacy-checkbox"
        />
      </div>
      <div className="sm:col-span-2 flex flex-wrap items-center gap-5">
        <button
          type="submit"
          disabled={sending}
          data-testid="sponsor-submit-button"
          className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#E0708A] px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#5E2A52] disabled:opacity-60"
        >
          {sending ? "Invio in corso..." : "Invia candidatura sponsor"}
          <Send size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
        </button>
        <p className="inline-flex items-center gap-2 text-xs text-[#6B5E62]">
          <ShieldCheck size={14} className="text-[#5E2A52]" />
          Dati conservati su server ad accesso riservato
        </p>
      </div>
      {error && (
        <p
          className="sm:col-span-2 rounded-xl bg-red-50 px-5 py-3 text-sm text-red-700"
          data-testid="sponsor-error-message"
        >
          {error}
        </p>
      )}
      {done && (
        <p
          className="sm:col-span-2 rounded-xl bg-[#5E2A52] px-5 py-4 text-sm text-white"
          data-testid="sponsor-success-message"
        >
          Grazie! Abbiamo ricevuto la candidatura: ti risponderemo all'indirizzo indicato.
        </p>
      )}
    </motion.form>
  );
};

export const JoinForm = () => {
  const [openSponsor, setOpenSponsor] = useState(false);

  return (
    <section
      id="aderisci"
      className="relative mx-auto max-w-[1400px] px-5 py-16 sm:px-10 sm:py-20 lg:py-28"
      data-testid="join-section"
    >
      <div className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E0708A]">
          Partecipa
        </p>
        <h2 className="mt-4 font-display text-3xl leading-tight tracking-tight text-[#1A1617] sm:text-4xl">
          Due modi per essere
          <br />
          <span className="di-shine italic">parte della rete</span>.
        </h2>
      </div>

      <div className="mt-12 grid gap-6 lg:grid-cols-2">
        <div
          className={`group overflow-hidden rounded-[26px] border transition-colors duration-500 ${
            openSponsor ? "border-[#E0708A]/60 bg-[#5E2A52]" : "border-[#E8E2E4] bg-white"
          } shadow-[0_14px_50px_rgba(94,42,82,0.08)]`}
        >
          <button
            type="button"
            onClick={() => setOpenSponsor((v) => !v)}
            data-testid="open-sponsor-form"
            className="block w-full text-left"
          >
            <div className="relative h-44 overflow-hidden sm:h-52">
              <img
                src={CARD_SPONSOR}
                alt="Composizione astratta che rappresenta la partnership con DonnaImpresa"
                className="h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.07]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3E1B36]/85 via-[#5E2A52]/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6">
                <div>
                  <p className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-md">
                    <Handshake size={12} /> Aziende
                  </p>
                  <h3 className="mt-3 font-display text-2xl text-white sm:text-3xl">
                    Diventa sponsor
                  </h3>
                </div>
                <span
                  className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-all duration-500 ${
                    openSponsor
                      ? "rotate-180 bg-[#E0708A] text-white"
                      : "bg-white/90 text-[#5E2A52] group-hover:bg-[#E0708A] group-hover:text-white"
                  }`}
                >
                  {openSponsor ? <Check size={17} /> : <ArrowRight size={17} />}
                </span>
              </div>
            </div>
          </button>

          <div className="p-7 sm:p-8">
              <p
                className={`max-w-sm text-sm leading-relaxed ${
                  openSponsor ? "text-white/75" : "text-[#6B5E62]"
                }`}
              >
                La tua azienda entra nella vetrina del sito e nel racconto
                social della rete.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  ["Logo e link nella vetrina sponsor", "Il logo viene pubblicato nella sezione Sponsor con link diretto al tuo sito."],
                  ["Contenuto dedicato su Instagram", "Un post o una storia dedicata sul profilo Instagram di DonnaImpresa per presentare l'azienda."],
                  ["Presenza agli incontri della rete", "Inviti agli incontri e ai tavoli di lavoro con le imprenditrici della community."],
                ].map(([v, tip]) => (
                  <li
                    key={v}
                    className={`flex items-start gap-2.5 text-xs ${
                      openSponsor ? "text-white/85" : "text-[#6B5E62]"
                    }`}
                  >
                    <Check size={13} className="mt-0.5 shrink-0 text-[#E0708A]" />
                    <span className="flex items-center gap-1.5">
                      {v}
                      <InfoTip text={tip} testId={`tip-sponsor-${v.slice(0, 8).toLowerCase().replace(/ /g, "-")}`} />
                    </span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => setOpenSponsor((v) => !v)}
                data-testid="open-sponsor-form-cta"
                className={`mt-7 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] ${
                  openSponsor ? "text-[#E0708A]" : "text-[#5E2A52]"
                }`}
              >
                {openSponsor ? "Modulo aperto qui sotto" : "Apri il modulo sponsor"}
                <ArrowRight
                  size={13}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
          </div>

          <AnimatePresence initial={false}>
            {openSponsor && <SponsorForm onClose={() => setOpenSponsor(false)} />}
          </AnimatePresence>
        </div>

        <div className="group overflow-hidden rounded-[26px] border border-[#E8E2E4] bg-white shadow-[0_14px_50px_rgba(94,42,82,0.08)] transition-colors duration-500 hover:border-[#E0708A]/50">
          <Link
            to="/racconta-la-tua-storia"
            data-testid="open-story-page"
            className="block"
          >
          <div className="relative h-44 overflow-hidden sm:h-52">
            <img
              src={CARD_STORY}
              alt="Mani che scrivono i propri obiettivi: racconta la tua storia"
              className="h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.07]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#3E1B36]/85 via-[#5E2A52]/25 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6">
              <div>
                <p className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-md">
                  <BookOpenText size={12} /> Imprenditrici
                </p>
                <h3 className="mt-3 font-display text-2xl text-white sm:text-3xl">
                  Raccontaci la tua storia
                </h3>
              </div>
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/90 text-[#5E2A52] transition-colors duration-500 group-hover:bg-[#E0708A] group-hover:text-white">
                <ArrowRight size={17} />
              </span>
            </div>
          </div>
          </Link>

          <div className="p-7 sm:p-8">
            <p className="max-w-sm text-sm leading-relaxed text-[#6B5E62]">
              Condividi obiettivi ed esperienze: la tua storia diventa guida per
              altre donne.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                ["Pagina dedicata con il modulo racconto", "Un modulo completo, su una pagina propria ottimizzata per i motori di ricerca."],
                ["Intervista pubblicata nella Community", "Rivedi e approvi il testo prima della pubblicazione: nulla esce senza il tuo ok."],
                ["Rilancio della storia sui canali social", "La storia viene condivisa su Instagram per dare visibilità alla tua impresa."],
              ].map(([v, tip]) => (
                <li key={v} className="flex items-start gap-2.5 text-xs text-[#6B5E62]">
                  <Check size={13} className="mt-0.5 shrink-0 text-[#E0708A]" />
                  <span className="flex items-center gap-1.5">
                    {v}
                    <InfoTip text={tip} testId={`tip-storia-${v.slice(0, 8).toLowerCase().replace(/ /g, "-")}`} />
                  </span>
                </li>
              ))}
            </ul>
            <Link
              to="/racconta-la-tua-storia"
              data-testid="open-story-page-cta"
              className="mt-7 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#5E2A52]"
            >
              Vai alla pagina dedicata
              <ArrowRight
                size={13}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
